import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { usePathname, navigate } from "wouter/use-browser-location";
import { useAuth } from "@/_core/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Bell,
  CalendarCheck,
  ClipboardCheck,
  ArrowRight,
  Clock,
  RefreshCw,
  Wrench,
  MapPin,
  DollarSign,
} from "lucide-react";

const TASK_TYPE_CONFIG: Record<
  string,
  { label: string; typeTag?: string; icon: React.ElementType; color: string; bgColor: string }
> = {
  scheduled_visit: {
    label: "Upcoming Site Visit",
    typeTag: "SV",
    icon: MapPin,
    color: "text-blue-600",
    bgColor: "bg-blue-50 border-blue-200",
  },
  pending_svr_review: {
    label: "SVR Awaiting Your Review",
    typeTag: "SV",
    icon: ClipboardCheck,
    color: "text-purple-600",
    bgColor: "bg-purple-50 border-purple-200",
  },
  follow_up_visit: {
    label: "Follow-up Visit Due",
    typeTag: "SV",
    icon: AlertCircle,
    color: "text-red-600",
    bgColor: "bg-red-50 border-red-200",
  },
  escalated_repair_order: {
    label: "Repair Order Overdue",
    typeTag: "RO",
    icon: Wrench,
    color: "text-red-700",
    bgColor: "bg-red-50 border-red-200",
  },
  pending_customer_notice: {
    label: "Notify Customer",
    typeTag: "NOTIF",
    icon: Bell,
    color: "text-amber-600",
    bgColor: "bg-amber-50 border-amber-200",
  },
  unassigned_svr_pic: {
    label: "No PIC Assigned",
    typeTag: "SV",
    icon: AlertTriangle,
    color: "text-orange-600",
    bgColor: "bg-orange-50 border-orange-200",
  },
  invoice_ready: {
    label: "Invoice Ready to Send",
    typeTag: "INV",
    icon: DollarSign,
    color: "text-emerald-700",
    bgColor: "bg-emerald-50 border-emerald-200",
  },
  unmatched_customer: {
    label: "Unmatched Customer",
    typeTag: "SV",
    icon: AlertTriangle,
    color: "text-amber-700",
    bgColor: "bg-amber-50 border-amber-200",
  },
  unmatched_site_resolution: {
    label: "Unmatched Site — Populate Customer Info",
    typeTag: "SV",
    icon: AlertTriangle,
    color: "text-amber-700",
    bgColor: "bg-amber-50 border-amber-200",
  },
  escalated_unmatched_site: {
    label: "Escalated Unmatched Site — PIC Follow-up Needed",
    typeTag: "SV",
    icon: AlertTriangle,
    color: "text-red-700",
    bgColor: "bg-red-50 border-red-200",
  },
};

function formatDate(d?: Date | string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-SG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function MyTasks() {
  const pathname = usePathname();
  const { user } = useAuth();
  const { data, isLoading, refetch, isFetching } =
    trpc.dashboard.getRoleBasedTasks.useQuery(undefined, {
      refetchOnWindowFocus: true,
    });

  // Server already deduplicates tasks by referenceNumber and provides _count.
  // Sort by daysOverdue descending (most overdue first).
  const tasks = [...(data?.tasks ?? [])].sort(
    (a, b) => (b.daysOverdue ?? 0) - (a.daysOverdue ?? 0)
  );

  const taskOpenPayload = (task: {
    type: string;
    id: number;
    referenceNumber?: string;
    noticeSourceType?: string;
  }) => ({
    type: task.type,
    id: task.id,
    referenceNumber: task.referenceNumber,
    noticeSourceType: task.noticeSourceType,
  });

  const handleTaskClick = (task: {
    type: string;
    id: number;
    customerId?: number;
    sourceType?: string;
    noticeSourceType?: string;
    noticeId?: number;
    needsCosting?: boolean;
    referenceNumber?: string;
  }) => {
    const navState = { from: pathname, taskOpen: taskOpenPayload(task) };
    if (
      task.type === "scheduled_visit" ||
      task.type === "pending_svr_review" ||
      task.type === "follow_up_visit" ||
      task.type === "unassigned_svr_pic"
    ) {
      const suffix = task.needsCosting ? "?costing=1" : "";
      navigate(`/site-visits/id/${task.id}${suffix}`, { state: navState });
    } else if (
      task.type === "unmatched_customer" ||
      task.type === "unmatched_site_resolution" ||
      task.type === "escalated_unmatched_site"
    ) {
      const suffix = task.needsCosting ? "&costing=1" : "";
      navigate(`/site-visits/id/${task.id}?matchCustomer=1${suffix}`, { state: navState });
    } else if (task.type === "escalated_repair_order") {
      navigate(`/repair-orders/id/${task.id}`, { state: navState });
    } else if (task.type === "invoice_ready") {
      if (task.noticeSourceType === "ro") {
        navigate(`/repair-orders/id/${task.id}`, { state: navState });
      } else {
        navigate(`/site-visits/id/${task.id}`, { state: navState });
      }
    } else if (task.type === "pending_customer_notice") {
      if (task.noticeSourceType === "ro") {
        navigate(`/repair-orders/id/${task.id}`, { state: navState });
      } else {
        navigate(`/site-visits/id/${task.id}`, { state: navState });
      }
    }
  };

  const roleLabel =
    user?.role === "admin"
      ? "Admin"
      : user?.role === "sales"
      ? "Salesperson"
      : user?.role === "service"
      ? "Service Technician"
      : user?.role === "programming"
      ? "Programmer"
      : user?.role === "installer"
      ? "Installer"
      : "Staff";

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">My Tasks</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {user?.name ? `${user.name} · ` : ""}
              {roleLabel} — items requiring your attention
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 border rounded-md hover:bg-gray-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Task count summary */}
        {!isLoading && (
          <div className="flex items-center gap-2">
            {tasks.length === 0 ? (
              <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" />
                All caught up — no pending tasks
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                  {tasks.length}
                </span>
                <span className="text-muted-foreground">
                  {tasks.length === 1 ? "task" : "tasks"} pending
                </span>
              </div>
            )}
          </div>
        )}

        {/* Task list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Pending Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <CheckCircle2 className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm font-medium">No tasks right now</p>
                <p className="text-xs mt-1 opacity-70">
                  Check back later or refresh to see new items
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {tasks.map((task: any) => {
                  const config =
                    TASK_TYPE_CONFIG[task.type] ?? TASK_TYPE_CONFIG.scheduled_visit;
                  const Icon = config.icon;
                  const isClickable = !!task.id;

                  return (
                    <div
                      key={`${task.type}-${task.id}`}
                      onClick={() => handleTaskClick(task)}
                      className={`flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors group`}
                    >
                      {/* Type icon */}
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border ${config.bgColor}`}
                      >
                        <Icon className={`h-4 w-4 ${config.color}`} />
                      </div>

                      {/* Task info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {config.typeTag && (
                            <span className="inline-flex items-center text-xs font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 tracking-wide">
                              {config.typeTag}
                            </span>
                          )}
                          <span
                            className={`text-xs font-semibold uppercase tracking-wide ${config.color}`}
                          >
                            {config.label}
                          </span>
                          {task.referenceNumber && (
                            <span className="font-mono text-xs text-muted-foreground">
                              {task.referenceNumber}
                            </span>
                          )}
                          {task.daysOverdue !== undefined && (
                            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">
                              <Clock className="h-3 w-3" />
                              {task.daysOverdue}d overdue
                            </span>
                          )}
                          {(task._count ?? 1) > 1 && (
                            <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                              +{(task._count ?? 1) - 1} more
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium mt-0.5 truncate">
                          {task.customerName ||
                            task.title
                              .replace(/^(OVERDUE: )?(Complete customer details|Site visit scheduled|Follow-up visit due|SVR pending review): /, "")
                              .replace(/^OVERDUE: /, "")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {task.visitDate
                            ? `Visit: ${formatDate(task.visitDate)}`
                            : task.description}
                          {task.technicianName
                            ? ` · By ${task.technicianName}`
                            : ""}
                        </p>
                      </div>

                      {/* Arrow */}
                      <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help text */}
        {tasks.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Click any task to open the relevant record. Tasks update automatically when you complete them.
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}
